/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChild, Fragment, Ref } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { ButtonLayout } from '../UNSAFE_ButtonLayout';
import { forwardRef } from 'preact/compat';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLButtonElement>, 'autofocus'>;

/**
 * Note that only the ghost variant supports the xs size.
 */
type VariantSizeProps =
  | {
      /**
       * Indicates in what states the button has chrome (background and border).
       *     "ghost": "Ghost buttons are the least prominent variant. Ghost buttons are
       * useful for performing low-priority tasks, such as manipulating the UI.",
       *     "borderless"': "Borderless buttons are a more prominent variant. Borderless
       * buttons are useful for supplemental actions that require minimal emphasis.",
       *     "outlined"': "Outlined buttons are salient, but lighter weight than
       * solid buttons. Outlined buttons are useful for secondary actions.",
       *     "solid"': "Solid buttons stand out, and direct the user's attention to the
       *  most important actions in the UI.",
       *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
       * complete the action that is the main goal of the page or page section. There
       * should only be one CTA button on a page at any given time.",
       *     "danger"': "A Danger button alerts the user to a dangerous situation."
       */
      variant?: 'ghost';
      /**
       * The size indicates how large the button is rendered.
       */
      size?: 'xs' | 'sm' | 'md' | 'lg';
    }
  | {
      variant?: 'borderless' | 'outlined' | 'solid' | 'callToAction' | 'danger';

      size?: 'sm' | 'md' | 'lg';
    };

type BaseProps = IntrinsicProps & {
  /**
   * Specifies the icon for the button.
   */
  children?: ComponentChild;

  /**
   * Specifies that the button element should be disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies if repeated events are dispatched for key or pointer held down
   */
  isRepeat?: boolean;

  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the button.
   */
  'aria-describedby'?: string;

  /**
   * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
   */
  onAction?: () => void | null;
};

/**
 * Note that either an accessibleLabel or tooltip must be specified.
 */
type VariationAccessibleLabelProps =
  | {
      /**
       * accessibleLabel - the aria label
       */
      'aria-label': string;

      /**
       * tooltip - overrides the accessibleLabel for the tooltip
       */
      tooltip?: string;
    }
  | {
      'aria-label'?: string;

      tooltip: string;
    };

export const IconButton = forwardRef(
  (
    {
      variant = 'outlined',
      isDisabled = false,
      isRepeat = false,
      size = 'md',
      autofocus,
      onAction,
      tooltip,
      'aria-label': accessibleLabel,
      'aria-describedby': ariaDescribedBy,
      ...props
    }: BaseProps & VariantSizeProps & VariationAccessibleLabelProps,
    ref?: Ref<HTMLButtonElement>
  ) => {
    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltip ? tooltip : accessibleLabel
    });
    return (
      <Fragment>
        <BaseButton
          {...tooltipProps}
          ref={ref as Ref<HTMLButtonElement> & Ref<HTMLAnchorElement> & Ref<HTMLSpanElement>}
          isDisabled={isDisabled}
          isRepeat={isRepeat}
          size={size}
          autofocus={autofocus}
          variant={variant}
          styling={['min']}
          onAction={onAction}
          aria-describedby={ariaDescribedBy}
          aria-label={accessibleLabel ?? tooltip}>
          <ButtonLayout display={'icons'} startIcon={props.children} size={size}></ButtonLayout>
        </BaseButton>
        {tooltipContent}
      </Fragment>
    );
  }
);
